import {
  getOriginalFileUrl,
  getStoredPreviewUrl,
} from "../../../utils/filePreview";

export { getStoredPreviewUrl };

const IMG_EXT_RX = /\.(png|jpe?g|gif|webp|bmp|svg|avif)(?:$|[?#])/i;
const PDF_EXT_RX = /\.pdf(?:$|[?#])/i;
const EPS_EXT_RX = /\.eps(?:$|[?#])/i;

const toTrimmedString = (value) => {
  if (value == null) return "";
  const text = String(value).trim();
  return text || "";
};

const getFirstString = (value = {}, keys = []) => {
  for (const key of keys) {
    const text = toTrimmedString(value?.[key]);
    if (text) return text;
  }
  return "";
};

const safeObject = (value = {}) => {
  if (!value) return {};
  if (typeof value === "object") return value;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      return {};
    }
  }
  return {};
};

export const normalizePurchaseOrderDecorations = (item = {}) => {
  const decorations =
    [
      item?.generatedPurchaseOrderDecorations,
      item?.decorations,
      item?.Decorations,
      item?.Decoration,
    ].find((candidate) => Array.isArray(candidate) && candidate.length > 0) || [];

  return (Array.isArray(decorations) ? decorations : []).map((decoration, index) => {
    const details = safeObject(
      decoration?.DecoratorDetails ?? decoration?.decoratorDetails
    );
    const source = String(
      details?.source ?? details?.Source ?? decoration?.source ?? ""
    )
      .trim()
      .toLowerCase();

    return {
      ...decoration,
      DecoratorDetails: {
        ...details,
        source,
      },
      isSupplierCustom:
        decoration?.isSupplierCustom === true || source === "supplier-custom",
      __scopeIndex: decoration?.__scopeIndex ?? index,
    };
  });
};

export const getPurchaseOrderItemArtworks = (item = {}) =>
  [
    item?.ArtWorks,
    item?.artWorks,
    item?.artworks,
    item?.Artwork,
  ].find((candidate) => Array.isArray(candidate) && candidate.length > 0) || [];

// Legacy Sales Order snapshots may store real artwork only on approved-line
// products. Never replace a usable current PO artwork collection.
const hasArtworkSource = (artwork) =>
  Boolean(artwork && typeof artwork === "object" &&
    (getArtworkPreviewSources(artwork).originalFileUrl ||
     getArtworkPreviewSources(artwork).storedPreviewUrl));

export const resolvePurchaseOrderArtworks = (item = {}, supplierInfo = {}) => {
  const current = getPurchaseOrderItemArtworks(item);
  if (current.some(hasArtworkSource)) return current;

  const itemId = toTrimmedString(item?.orderItemId ?? item?.OrderItemId ?? item?.id);
  if (!itemId) return current;
  const lines = Array.isArray(supplierInfo?.approvedLines) ? supplierInfo.approvedLines : [];
  const approved = lines.map((entry) => entry?.line || entry).find((line) =>
    toTrimmedString(line?.orderItemId ?? line?.OrderItemId ?? line?.DBItemId) === itemId
  );
  if (!approved) return current;
  const recovered = getPurchaseOrderItemArtworks(approved).filter(hasArtworkSource);
  return recovered.length ? recovered : current;
};

export const getDecorationId = (decoration = {}) =>
  toTrimmedString(
    decoration?.id ??
      decoration?.Id ??
      decoration?.decorationId ??
      decoration?.DecorationId
  );

export const getArtworkDecorationId = (artwork = {}) =>
  toTrimmedString(artwork?.DecorationId ?? artwork?.decorationId);

export const getArtworkId = (artwork = {}) =>
  toTrimmedString(artwork?.id ?? artwork?.artworkId ?? artwork?.ArtWorkId);

const preserveFilledArtworkFields = (base = {}, overlay = {}) => ({
  ...base,
  ...Object.fromEntries(
    Object.entries(overlay).filter(([, value]) =>
      value !== null && value !== undefined && (typeof value !== "string" || value.trim() !== "")
    )
  ),
});

export const attachArtworkToDecorations = ({
  decorations = [],
  artworks = [],
} = {}) =>
  normalizePurchaseOrderDecorations({ generatedPurchaseOrderDecorations: decorations }).map(
    (decoration, index) => {
      const decorationId = getDecorationId(decoration);
      const matchedArtwork =
        artworks.find(
          (artwork) =>
            decorationId && getArtworkDecorationId(artwork) === decorationId
        ) ||
        decoration?.ArtWork ||
        decoration?.artwork ||
        artworks[index] ||
        null;

      if (!matchedArtwork || typeof matchedArtwork !== "object") {
        return decoration;
      }

      return {
        ...decoration,
        ArtWork: preserveFilledArtworkFields(
          matchedArtwork,
          decoration?.ArtWork || decoration?.artwork || {}
        ),
      };
    }
  );

// Rendered PO artwork must use the same populated source as preview diagnostics.
// A blank decoration placeholder is not artwork; preserve current valid artwork first.
export const resolveRenderedDecorationArtwork = (decoration = {}, sourceIndex = 0, itemArtworks = []) => {
  const hasSource = (value) => Boolean(value && typeof value === "object" &&
    (getArtworkPreviewSources(value).originalFileUrl || getArtworkPreviewSources(value).storedPreviewUrl));
  const embedded = decoration?.ArtWork || decoration?.artwork || null;
  if (hasSource(embedded)) return embedded;
  const decorationId = getDecorationId(decoration);
  const matching = itemArtworks.find((artwork) =>
    decorationId && getArtworkDecorationId(artwork) === decorationId && hasSource(artwork)
  );
  if (matching) return {
    ...matching,
    ...Object.fromEntries(Object.entries(embedded || {}).filter(([, value]) =>
      value != null && (typeof value !== "string" || value.trim() !== "")
    )),
  };
  const indexed = itemArtworks[sourceIndex];
  const indexMatches = !decorationId || getArtworkDecorationId(indexed) === decorationId;
  return indexMatches && hasSource(indexed) ? indexed : embedded || {};
};

export const selectDecorationsForPurchaseOrder = ({
  purchaseOrderType = "",
  supplierInfo = {},
  item = {},
}) => {
  const info = safeObject(supplierInfo);
  const type = String(purchaseOrderType || info?.type || "").trim();
  const decorations = normalizePurchaseOrderDecorations(item);
  const poDecoratorId =
    info?.decoratorId != null
      ? String(info.decoratorId).trim()
      : "";

  if (type === "Different_supplier_decoration") {
    if (!poDecoratorId) return [];
    return decorations.filter((decoration) => {
      const details = safeObject(
        decoration?.DecoratorDetails ?? decoration?.decoratorDetails
      );
      return (
        details?.decoratorId != null &&
        String(details.decoratorId).trim() === poDecoratorId
      );
    });
  }

  if (type === "Same_supplier_decoration") {
    return decorations.filter((decoration) => {
      const details = safeObject(
        decoration?.DecoratorDetails ?? decoration?.decoratorDetails
      );
      return decoration?.isSupplierCustom === true || details?.decoratorId == null;
    });
  }

  if (type === "Combined_supplier_and_external_decoration") {
    return decorations.filter((decoration) => {
      const details = safeObject(
        decoration?.DecoratorDetails ?? decoration?.decoratorDetails
      );
      return (
        decoration?.isSupplierCustom === true ||
        (poDecoratorId &&
          String(details?.decoratorId ?? "").trim() === poDecoratorId)
      );
    });
  }

  return [];
};

export const normalizeRenderPreviewIdentityUrl = (value = "") => {
  const text = toTrimmedString(value);
  if (!text) return "";

  try {
    const parsed = new URL(text);
    parsed.hash = "";
    return parsed.toString();
  } catch {
    return text.split("#")[0].trim();
  }
};

const normalizePreviewStatus = (value) => toTrimmedString(value).toLowerCase();
const normalizePreviewSourceType = (value) => {
  const normalized = toTrimmedString(value).toLowerCase();
  return normalized === "pdf" || normalized === "eps" ? normalized : "";
};

const isProofLikeRenderPreviewId = (value = "") => {
  const text = toTrimmedString(value).toLowerCase();
  if (!text) return false;
  return (
    text.startsWith("proof:") ||
    text.includes("po-proof-") ||
    text.startsWith("artwork:po-proof-")
  );
};

const hasUsablePreviewUrl = (value = "") => {
  const text = toTrimmedString(value);
  if (!text) return false;
  return !/^javascript:/i.test(text);
};

export const isBrowserImage = (value = "") => IMG_EXT_RX.test(toTrimmedString(value));

export const isPdf = (value = "") => PDF_EXT_RX.test(toTrimmedString(value));

export const isEps = (value = "") => EPS_EXT_RX.test(toTrimmedString(value));

export const getRenderPreviewSourceType = (value = "") => {
  if (value && typeof value === "object") {
    const persisted = normalizePreviewSourceType(
      value?.previewSourceType ?? value?.PreviewSourceType
    );
    if (persisted) return persisted;
    return getRenderPreviewSourceType(getArtworkPreviewSources(value).originalFileUrl);
  }

  const originalFileUrl = toTrimmedString(value);
  if (isPdf(originalFileUrl)) return "pdf";
  if (isEps(originalFileUrl)) return "eps";
  return "";
};

export const getArtworkRenderPreviewId = (artwork = {}) => {
  const artworkId = getArtworkId(artwork);
  if (artworkId) return `artwork:${artworkId}`;

  const explicit = getFirstString(artwork, ["renderPreviewId", "RenderPreviewId"]);
  if (explicit && !isProofLikeRenderPreviewId(explicit)) return explicit;

  const { originalFileUrl } = getArtworkPreviewSources(artwork);
  const decorationId = toTrimmedString(artwork?.DecorationId ?? artwork?.decorationId);
  const orderItemId = toTrimmedString(
    artwork?.OrderItemId ?? artwork?.orderItemId ?? artwork?.line?.id
  );
  const normalizedOriginalFileUrl = normalizeRenderPreviewIdentityUrl(originalFileUrl);
  return ["artwork", decorationId, orderItemId, normalizedOriginalFileUrl].join("|");
};

export const getPreparedRenderPreviewUrl = (artwork = {}) => {
  if (typeof window === "undefined") return "";
  const renderPreviewId = getArtworkRenderPreviewId(artwork);
  if (!renderPreviewId) return "";
  const map = window.__PO_RENDER_PREVIEW_CONFIG__?.previewUrlById || {};
  return toTrimmedString(map?.[renderPreviewId]);
};

export const hasUsableStoredPreview = ({
  storedPreviewUrl = "",
  previewStatus = "",
} = {}) => {
  if (!hasUsablePreviewUrl(storedPreviewUrl)) return false;
  return !previewStatus || previewStatus === "ready";
};

export const resolveOriginalOnlyStrategy = (originalFileUrl = "") => {
  if (isBrowserImage(originalFileUrl) && !isPdf(originalFileUrl)) {
    return {
      type: "original-image",
      displayUrl: originalFileUrl,
      originalFileUrl,
    };
  }

  if (isPdf(originalFileUrl)) {
    return {
      type: "legacy-pdf",
      displayUrl: "",
      originalFileUrl,
    };
  }

  return {
    type: "file-fallback",
    displayUrl: "",
    originalFileUrl,
  };
};

export const resolveArtworkDisplayStrategy = (artwork = {}) => {
  const {
    originalFileUrl,
    storedPreviewUrl,
    previewStatus,
  } = getArtworkPreviewSources(artwork);

  if (hasUsableStoredPreview({ storedPreviewUrl, previewStatus })) {
    return {
      type: "stored-preview",
      displayUrl: storedPreviewUrl,
      originalFileUrl,
      storedPreviewUrl,
      previewStatus,
      renderPreviewSourceType: getRenderPreviewSourceType(artwork),
    };
  }

  return {
    ...resolveOriginalOnlyStrategy(originalFileUrl),
    storedPreviewUrl,
    previewStatus,
    renderPreviewSourceType: getRenderPreviewSourceType(artwork),
  };
};

export const mergeApprovedProofIntoArtwork = ({
  artwork = {},
  approvedProof = null,
  proofLink = "",
} = {}) => {
  if (!approvedProof?.filePath) return artwork;
  const artworkRenderPreviewId = getArtworkRenderPreviewId(artwork);

  return {
    ...artwork,
    Image: approvedProof.filePath,
    FileUrl: approvedProof.filePath,
    previewImageUrl:
      getStoredPreviewUrl(artwork) ||
      null,
    renderPreviewId:
      artworkRenderPreviewId ||
      null,
    previewSourceType:
      normalizePreviewSourceType(
        artwork?.previewSourceType ?? artwork?.PreviewSourceType
      ) ||
      null,
    sourceContentType:
      toTrimmedString(approvedProof?.sourceContentType ?? approvedProof?.SourceContentType) ||
      toTrimmedString(artwork?.sourceContentType ?? artwork?.SourceContentType) ||
      null,
    Source: "ApprovedProof",
    ProofLink: proofLink,
    ApprovedAt: approvedProof.updatedAt || new Date().toISOString(),
    approvedProofUrl:
      getStoredPreviewUrl(approvedProof) ||
      toTrimmedString(approvedProof?.filePath ?? approvedProof?.proofFilePath) ||
      artwork?.approvedProofUrl ||
      null,
    approvedProofStatus:
      normalizePreviewStatus(
        approvedProof?.previewStatus ??
        approvedProof?.PreviewStatus ??
        approvedProof?.proofStatus
      ) ||
      artwork?.approvedProofStatus ||
      null,
  };
};

export const mergeApprovedProofIntoDecorationArtwork = ({
  decoration = {},
  approvedProof = null,
} = {}) => {
  if (!approvedProof?.filePath) return decoration;
  const existingArtwork = decoration.ArtWork || {};
  const artworkRenderPreviewId = getArtworkRenderPreviewId(existingArtwork);

  return {
    ...decoration,
    ArtWork: {
      ...existingArtwork,
      Image: approvedProof.filePath,
      FileUrl: approvedProof.filePath,
      previewImageUrl:
        getStoredPreviewUrl(existingArtwork) ||
        null,
      renderPreviewId:
        artworkRenderPreviewId ||
        null,
      previewSourceType:
        normalizePreviewSourceType(
          existingArtwork?.previewSourceType ?? existingArtwork?.PreviewSourceType
        ) ||
        null,
      sourceContentType:
        toTrimmedString(
          existingArtwork?.sourceContentType ?? existingArtwork?.SourceContentType
        ) ||
        null,
      approvedProofUrl:
        getStoredPreviewUrl(approvedProof) ||
        toTrimmedString(approvedProof?.filePath ?? approvedProof?.proofFilePath) ||
        existingArtwork?.approvedProofUrl ||
        null,
      approvedProofStatus:
        normalizePreviewStatus(
          approvedProof?.previewStatus ??
          approvedProof?.PreviewStatus ??
          approvedProof?.proofStatus
        ) ||
        existingArtwork?.approvedProofStatus ||
        null,
    },
  };
};

export const getArtworkPreviewSources = (artwork = {}) => {
  const originalFileUrl =
    getFirstString(artwork, [
      "Image",
      "image",
      "FileUrl",
      "fileUrl",
      "filePath",
      "proofFilePath",
      "url",
      "link",
    ]) || getOriginalFileUrl(artwork);
  const storedPreviewUrl = getFirstString(artwork, [
    "previewImageUrl",
    "previewUrl",
    "PreviewImageUrl",
    "thumbnailUrl",
    "ThumbnailUrl",
  ]) || getStoredPreviewUrl(artwork);
  const previewStatus = normalizePreviewStatus(
    artwork?.previewStatus ?? artwork?.PreviewStatus
  );
  const previewSourceType = normalizePreviewSourceType(
    artwork?.previewSourceType ?? artwork?.PreviewSourceType
  );

  return {
    originalFileUrl,
    storedPreviewUrl,
    previewStatus,
    previewSourceType,
  };
};
